import React from 'react';
import { Loader2, MessageSquare, Plus, Trash2 } from 'lucide-react';
import { ProjectChat } from '../../types';

interface ChatListProps {
  chats: ProjectChat[];
  activeChatId: string | null;
  loading?: boolean;
  canEdit?: boolean;
  onSelectChat: (chatId: string) => void;
  onNewChat: () => void;
  onDeleteChat?: (chatId: string) => void;
  deletingChatId?: string | null;
}

function formatTimestamp(value: Date): string {
  return value.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

export function ChatList({
  chats,
  activeChatId,
  loading = false,
  canEdit = true,
  onSelectChat,
  onNewChat,
  onDeleteChat,
  deletingChatId = null,
}: ChatListProps) {
  return (
    <div className="p-2">
      <div className="flex items-center justify-between px-2 py-1 mb-1">
        <span className="text-xs font-medium text-gray-500 uppercase">Chats</span>
        <button
          onClick={onNewChat}
          disabled={!canEdit}
          className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
          title="New chat"
        >
          <Plus className="h-4 w-4 text-gray-600" />
        </button>
      </div>

      {loading ? (
        <div className="px-3 py-4 text-sm text-gray-500">Loading chats...</div>
      ) : chats.length === 0 ? (
        <div className="text-center py-8">
          <MessageSquare className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No saved chats yet</p>
          <p className="text-xs text-gray-400 mt-1">Your first conversation will be saved automatically.</p>
        </div>
      ) : (
        <div className="space-y-1">
          {chats.map((chat) => (
            <div
              key={chat.id}
              className={`group rounded-lg border transition-colors ${
                chat.id === activeChatId
                  ? 'bg-blue-50 border-blue-200'
                  : 'bg-white border-transparent hover:border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start gap-2 px-3 py-2">
                <button
                  type="button"
                  onClick={() => onSelectChat(chat.id)}
                  className="min-w-0 flex-1 text-left"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-medium text-gray-800 truncate">{chat.title}</span>
                    <span className="text-[11px] text-gray-400 flex-shrink-0">
                      {formatTimestamp(chat.updatedAt)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {chat.messages.length} {chat.messages.length === 1 ? 'message' : 'messages'}
                  </p>
                </button>
                {canEdit && onDeleteChat && (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onDeleteChat(chat.id);
                    }}
                    disabled={deletingChatId === chat.id}
                    className="shrink-0 rounded p-1 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                    title={`Delete ${chat.title}`}
                    aria-label={`Delete ${chat.title}`}
                  >
                    {deletingChatId === chat.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
